import type { PairMessage } from "@cronika/proto/common/trading_pb";
import {
  Arrow,
  CategoryAll,
  CategoryCommodities,
  CategoryIndices,
  CategoryStocks,
  Dollar,
  Plus,
  Star,
} from "@cronika/assets/icons";
import { useTranslation } from "@cronika/locales";
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@cronika/ui";
import type { FC } from "react";
import { useCallback, useMemo, useState } from "react";
import { useMediaQuery } from "usehooks-ts";
import { AddPairDropdown } from "./components/AddPairDropdown";
import { AddPairMobilePanel } from "./components/AddPairMobilePanel";
import type { TAddPairProps, TAddCategory, TCategory } from "./types";
import { CATEGORY_ALL, PAIR_CATEGORY } from "./types";

export const AddPair: FC<TAddPairProps> = ({
  pairs = [],
  setSelectedPairs,
  selectPairAsActive,
  selectedPairs = [],
  favoritePairs = [],
  onToggleFavorite,
  activePair,
  mode = "add",
  onReplace,
  children,
  onOpenChange,
}) => {
  const { t } = useTranslation("pairs-control");
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const [selectedCategory, setSelectedCategory] = useState<TAddCategory>(CATEGORY_ALL);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const allCategories: TCategory[] = useMemo(
    () => [
      { key: CATEGORY_ALL, icon: CategoryAll, label: t("pairs-control.all-category") },
      { key: PAIR_CATEGORY.CRYPTO, icon: Dollar, label: t("pairs-control.crypto-category") },
      { key: PAIR_CATEGORY.FOREX, icon: Arrow, label: t("pairs-control.forex-category") },
      { key: PAIR_CATEGORY.COMMODITY, icon: CategoryCommodities, label: t("pairs-control.commodity-category") },
      { key: PAIR_CATEGORY.STOCK, icon: CategoryStocks, label: t("pairs-control.stock-category") },
      { key: PAIR_CATEGORY.INDEX, icon: CategoryIndices, label: t("pairs-control.index-category") },
      { key: PAIR_CATEGORY.FAVORITE, icon: Star, label: t("pairs-control.favorite-category") },
    ],
    [t],
  );

  // Only show categories that actually have pairs (All + Favorites always visible)
  const categories = useMemo(
    () =>
      allCategories.filter(
        (cat) =>
          cat.key === CATEGORY_ALL || cat.key === PAIR_CATEGORY.FAVORITE || pairs.some((p) => p.type === cat.key),
      ),
    [allCategories, pairs],
  );

  const isPairSelected = useCallback((pairId: string) => selectedPairs.some((p) => p.id === pairId), [selectedPairs]);

  const onPairSelect = useCallback(
    (pair: PairMessage) => {
      if (mode === "replace") {
        onReplace?.(pair);
        setIsMobileOpen(false);
        return;
      }

      const isAlreadySelected = isPairSelected(pair.id);
      if (isAlreadySelected) {
        if (selectPairAsActive) {
          selectPairAsActive(pair.id);
        }
        setIsMobileOpen(false);
        return;
      }
      setSelectedPairs((prev) => [...prev, pair]);
      setIsMobileOpen(false);
    },
    [mode, onReplace, isPairSelected, selectPairAsActive, setSelectedPairs],
  );

  const onRemovePair = useCallback(
    (pairId: string) => {
      if (selectedPairs.length <= 1) return;
      setSelectedPairs((prev) => prev.filter((p) => p.id !== pairId));
    },
    [selectedPairs.length, setSelectedPairs],
  );

  const sharedProps = {
    pairs,
    categories,
    selectedCategory,
    onCategoryChange: setSelectedCategory,
    activePair,
    selectedPairs,
    favoritePairs,
    onToggleFavorite,
    selectPairAsActive,
    onPairSelect,
    onRemovePair,
  };

  const trigger = children ?? (
    <Button variant="bordered" size="lg" isIconOnly radius="lg" className="border-white border-small">
      <Plus />
    </Button>
  );

  if (isMobile) {
    return (
      <>
        <div
          onClick={() => {
            setIsMobileOpen(true);
            onOpenChange?.(true);
          }}
        >
          {trigger}
        </div>
        <AddPairMobilePanel
          isOpen={isMobileOpen}
          onClose={() => {
            setIsMobileOpen(false);
            onOpenChange?.(false);
          }}
          {...sharedProps}
        />
      </>
    );
  }

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[530px] max-h-[--radix-dropdown-menu-content-available-height] border-[#8C8C8C]/15 bg-[#8C8C8C]/15 p-0 backdrop-blur-xl"
        radius="xl"
        align="start"
      >
        <AddPairDropdown {...sharedProps} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
